import { useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Banknote, Printer } from "lucide-react";
import { BTN_SOFT, BTN_SM } from "./buttonStyles";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtINR, docUrl } from "@/lib/documents";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { GovtIdActions } from "./GovtIdActions";

const ICON = {
  pass: <CheckCircle2 size={18} strokeWidth={1.75} className="text-[#14C48E] shrink-0" />,
  fail: <XCircle size={18} strokeWidth={1.75} className="text-[#DC2626] shrink-0" />,
  warning: <AlertTriangle size={18} strokeWidth={1.75} className="text-[#F59E0B] shrink-0" />,
};

function MarkButton({ stayId, checkId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const mark = async () => {
    setBusy(true);
    try { await api.post(`/pms/stays/${stayId}/preflight/mark`, { check_id: checkId }); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <button type="button" disabled={busy} onClick={mark} data-testid={`preflight-mark-${checkId}`}
      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-[#D5E6E8] bg-white text-[12.5px] font-semibold text-[#014751] hover:bg-[#F4F8F9] disabled:opacity-60">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} strokeWidth={1.75} />}Mark
    </button>
  );
}

/** Step 1 · the pre-flight checklist. Hard blocks disable Continue; soft warnings need acknowledgement. */
export function PreflightStep({ pf, onChanged, onCollect, ack, setAck }) {
  const blocking = pf.checks.filter((c) => c.blocking && c.status !== "pass");
  const warnings = pf.checks.filter((c) => !c.blocking && c.status !== "pass");
  return (
    <div className="space-y-2" data-testid="preflight-checklist">
      {pf.checks.map((c) => (
        <div key={c.id} data-testid={`preflight-check-${c.id}`} data-status={c.status}
          className={`rounded-lg border px-3 py-2.5 ${c.status === "fail" ? "border-[#FECACA] bg-[#FEF2F2]" : c.status === "warning" ? "border-[#FDE68A] bg-[#FFFBEB]" : "border-[#D5E6E8] bg-white"}`}>
          <div className="flex items-start gap-3">
            {ICON[c.status] || ICON.warning}
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold text-[#1A2425]">
                {c.id === "outstanding_balance" && c.amount > 0 ? `Outstanding balance: ${fmtINR(c.amount)}` : c.id === "government_id" && c.status === "fail" ? "Government ID not collected" : c.label}
              </div>
              {c.detail && c.id !== "outstanding_balance" && <div className="text-[12px] text-[#3F5257] mt-0.5">{c.detail}</div>}
              {c.id === "government_id" && (c.status !== "pass" || c.document) && (
                <div className="mt-2 space-y-2"><GovtIdActions check={c} guestId={pf.guest_id} stayId={pf.stay_id} hotelId={pf.hotel_id} onChanged={onChanged} />
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => requestRegCard({ url: docUrl("registration_card", pf.stay_id), filename: "RegistrationCard.pdf" })} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#014751] hover:underline" data-testid="preflight-print-regcard"><Printer size={13} />Print Registration Card</button>
                    <DownloadPdfButton url={docUrl("registration_card", pf.stay_id)} filename="RegistrationCard.pdf" label="Download Registration Card" regCard testId="preflight-download-regcard" />
                  </div></div>
              )}
            </div>
            <div className="shrink-0">
              {c.action === "mark" && <MarkButton stayId={pf.stay_id} checkId={c.id} onChanged={onChanged} />}
              {c.action === "collect_payment" && (
                <button type="button" onClick={onCollect} data-testid="preflight-collect-payment"
                  className={`${BTN_SOFT} ${BTN_SM}`}>
                  <Banknote size={14} strokeWidth={1.75} />Collect Payment
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {blocking.length > 0 && (
        <div className="text-[12.5px] font-semibold text-[#991B1B]" data-testid="preflight-blocking-count">
          {blocking.length} item{blocking.length === 1 ? "" : "s"} need{blocking.length === 1 ? "s" : ""} attention before you can continue
        </div>
      )}
      {blocking.length === 0 && warnings.length > 0 && (
        <label className="flex items-start gap-2 rounded-md bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 text-[12.5px] text-[#78530E] cursor-pointer" data-testid="preflight-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="preflight-ack-checkbox" />
          <span>I acknowledge the {warnings.length} unresolved warning{warnings.length === 1 ? "" : "s"} ({warnings.map((w) => w.label.toLowerCase()).join(", ")}) and want to proceed.</span>
        </label>
      )}
    </div>
  );
}
